package org.hs_soft.runmynesto.pages.components

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import org.hs_soft.runmynesto.domain.config.*
import org.hs_soft.runmynesto.domain.model.general.*
import org.hs_soft.runmynesto.domain.util.*
import org.hs_soft.runmynesto.domain.util.helper.Strings
import org.hs_soft.runmynesto.pages.components.grid_function.GridFooter
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun CustomTable(
    modifier: Modifier = Modifier,
    columns: List<TableColumn>,
    width: CSSSizeValue<CSSUnit.px>? = null,
    height: CSSSizeValue<CSSUnit.px>? = null,
    selectedId: String? = null,
    itemBackground: (Map<String, Any?>) -> CSSColorValue = { Theme.Transparent.rgb },
    minHeight: CSSSizeValue<CSSUnit.px> = Dimen.tableMinHeight,
    data: List<Map<String, Any?>>?,
    footerType: FooterType = FooterType.STANDARD,
    footerData: Map<String, String>? = null,
    isCustom: Boolean = false,
    showRefreshButton: Boolean = true,
    onRefresh: () -> Unit = {},
    inactiveItem: (Map<String, Any?>) -> Boolean = { false },
    onRowClick: (String, Map<String, Any?>) -> Unit = { _, _ -> },
) {
    var sortedColumn by remember { mutableStateOf<String?>(null) }
    var sortDirection by remember { mutableStateOf(SortDirection.NONE) }

    val sortedData = getSortedData(data, columns, sortedColumn, sortDirection)
    val minTableWidth = if (width != null && width.value.toInt() > columns.size * 100)
        width
    else columns.size * 100.px

    Column(
        modifier = modifier
            .position(Position.Relative)
            .minHeight(minHeight)
            .fillMaxWidth()
            .fillMaxHeight()
            .thenIf(
                height!=null,
                Modifier.height(height?:0.px)
            )
            .thenIf(
                width != null,
                modifier.width(width ?: 0.px)
            )
            .borderRadius(Dimen.semiLargeRadius)
            .overflow(Overflow.Hidden)
            .border(
                width = Dimen.smallThickness,
                style = LineStyle.Solid,
                color = Theme.ActionDisabledBackground.rgb
            )
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .fillMaxHeight()
                .weight(1f)
                .styleModifier {
                    property("overflow-x", "auto")
                    property("overflow-y", "auto")
                    property("-webkit-overflow-scrolling", "touch")
                }
        ) {
            Table(
                attrs = {
                    style {
                        if (!isCustom) {
                            minWidth(minTableWidth)
                        }
                        if (data.isNullOrEmpty())
                            height(100.percent)
                        width(100.percent)
                        borderCollapse(BorderCollapse.Collapse)
                        property("table-layout", "fixed")
                    }
                }
            ) {
                TableHeader(
                    columns = columns,
                    isCustom = isCustom,
                    sortedColumn = sortedColumn,
                    sortDirection = sortDirection,
                    onSortChange = { column, direction ->
                        sortedColumn = column
                        sortDirection = direction
                    }
                )

                if (data.isNullOrEmpty()) {
                    Tbody(
                        attrs = Modifier
                            .height(100.percent)
                            .toAttrs()
                    ) {
                        Tr {
                            Td(
                                attrs = {
                                    style {
                                        height(100.percent)
                                        property("text-align", "center")
                                    }
                                    attr("colspan", columns.size.toString())
                                }
                            ) {
                                ShowWhenListIsEmpty()
                            }
                        }
                    }
                } else {
                    TableBody(
                        columns = columns,
                        data = sortedData,
                        isCustom = isCustom,
                        itemBackground=itemBackground,
                        selectedId = selectedId,
                        inactiveItem = inactiveItem,
                        onRowClick = onRowClick
                    )
                }
            }
        }

        when (footerType) {
            FooterType.STANDARD -> {
                GridFooter(
                    title = Strings.current().items,
                    dataSize = data?.size ?: 0,
                    width = null,
                    showRefreshIcon = showRefreshButton,
                    onRefreshClick = onRefresh
                )
            }
            FooterType.ALIGNED -> {
                AlignedFooter(
                    columns = columns,
                    footerData = footerData ?: emptyMap()
                )
            }
            FooterType.NONE -> {}
        }
    }
}


@Composable
private fun TableHeader(
    columns: List<TableColumn>,
    isCustom: Boolean = false,
    sortedColumn: String?,
    sortDirection: SortDirection,
    onSortChange: (String?, SortDirection) -> Unit
) {
    Thead {
        Tr(
            attrs = {
                style {
                    height(Dimen.gridHeaderHeight)
                    backgroundColor(Theme.SolidActionHover.rgb)
                    zIndex(Constants.ZIndex.normalZIndex)
                    property("position", "sticky")
                    property("top", "0")
                    property("z-index", "1")
                }
            }
        ) {
            columns.forEach { column ->
                TableHeaderCell(
                    column = column,
                    isCustom = isCustom,
                    isCurrentlySorted = column.key == sortedColumn,
                    currentSortDirection = sortDirection,
                    onSort = { newDirection ->
                        val newSortedColumn = if (newDirection == SortDirection.NONE) null else column.key
                        onSortChange(newSortedColumn, newDirection)
                    }
                )
            }
        }
    }
}

@Composable
private fun TableHeaderCell(
    column: TableColumn,
    isCustom: Boolean = false,
    isCurrentlySorted: Boolean,
    currentSortDirection: SortDirection,
    onSort: (SortDirection) -> Unit
) {
    Th(
        attrs = {
            style {
                if (isCustom) {
                    width((column.width ?: 100).px)
                }
                paddingRight(Dimen.largePadding)
                paddingLeft(Dimen.largePadding)
                borderBottom(
                    width = Dimen.smallThickness,
                    style = LineStyle.Solid,
                    color = Theme.ActionDisabledBackground.rgb
                )
                if (column.isSortable) {
                    cursor(Cursor.Pointer)
                }
                textAlign(
                    if (column.isNumeric) TextAlign.Right
                    else TextAlign.Left
                )
                whiteSpace(WhiteSpace.NoWrap)
                overflow(Overflow.Hidden)
                textOverflow(TextOverflow.Ellipsis)
            }
            if (column.isSortable) {
                onClick {
                    val newDirection = if (isCurrentlySorted) {
                        when (currentSortDirection) {
                            SortDirection.NONE -> SortDirection.ASC
                            SortDirection.ASC -> SortDirection.DESC
                            SortDirection.DESC -> SortDirection.NONE
                        }
                    } else {
                        SortDirection.ASC
                    }
                    onSort(newDirection)
                }
            }
        }
    ) {
        HeaderContent(
            title = column.header,
            isNumeric = column.isNumeric,
            showSortIcon = isCurrentlySorted && column.isSortable,
            sortDirection = currentSortDirection
        )
    }
}

@Composable
private fun HeaderContent(
    title: String,
    isNumeric: Boolean,
    showSortIcon: Boolean,
    sortDirection: SortDirection
) {
    Row(
        modifier = Modifier
            .height(Dimen.gridHeaderHeight)
            .fillMaxWidth(),
        horizontalArrangement = if (isNumeric) Arrangement.End else Arrangement.Start,
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            text = title,
            modifier = Modifier
                .cursor(Cursor.Pointer)
                .fontWeight(FontWeight.Medium)
                .fontSize(FontSizes.mediumFontSize)
                .lineHeight(FontLineHeight.lineMedium)
                .letterSpacing(LetterSpacing.semiMedium)
                .fontFamily(FontFamily.Jakarta)
                .overflowText()
                .color(Theme.TextPrimary.rgb)
        )

        if (showSortIcon) {
            Box(
                modifier = Modifier
                    .size(Dimen.mediumIconSize.px)
                    .margin(left = Dimen.smallPadding)
            ) {
                VectorIcon(
                    modifier = Modifier.padding(Dimen.smallPadding),
                    pathData = when (sortDirection) {
                        SortDirection.NONE -> Res.PathIcon.sortAsc
                        SortDirection.ASC -> Res.PathIcon.sortAsc
                        SortDirection.DESC -> Res.PathIcon.sortDesc
                    },
                    height = Dimen.mediumIconSize,
                    width = Dimen.mediumIconSize,
                    color = Theme.TextSecondary.hex,
                    visibilityStatus = true
                )
            }
        }
    }
}

@Composable
private fun TableBody(
    columns: List<TableColumn>,
    data: List<Map<String, Any?>>?,
    itemBackground: (Map<String, Any?>) -> CSSColorValue,
    isCustom: Boolean = false,
    selectedId: String?,
    inactiveItem: (Map<String, Any?>) -> Boolean,
    onRowClick: (String, Map<String, Any?>) -> Unit
) {
    Tbody(
        attrs = {
            style {
                width(100.percent)
                property("overflow-x", "hidden")
                property("table-layout", "fixed")
                property("-webkit-overflow-scrolling", "touch")
            }
        }
    ) {
        data?.forEachIndexed { _, row ->
            val isInactive = inactiveItem(row)

            val directId = row["id"]?.toString()
            val categoryData = row["category"] as? Map<String, Any?>
            val categoryId = categoryData?.get("id")?.toString()

            val rowId = directId ?: categoryId ?: ""

            TableRow(
                isInactive = isInactive,
                isSelected = selectedId?.isNotEmpty() == true && selectedId == rowId,
                row = row,
                columns = columns,
                itemBackground = itemBackground(row),
                isCustom = isCustom,
                onRowClick = {
                    onRowClick(rowId, row)
                }
            )
        }
    }
}

@Composable
private fun TableRow(
    isSelected: Boolean,
    itemBackground: CSSColorValue,
    isInactive: Boolean,
    row: Map<String, Any?>,
    columns: List<TableColumn>,
    isCustom: Boolean = false,
    onRowClick: () -> Unit
) {
    var isHovered by remember { mutableStateOf(false) }

    Tr(
        attrs = Modifier
            .minHeight(Dimen.mediumTableItemHeight)
            .cursor(Cursor.Pointer)
            .position(Position.Relative)
            .overflow(Overflow.Hidden)
            .onMouseEnter { isHovered = true }
            .onMouseLeave { isHovered = false }
            .borderBottom(
                Dimen.smallThickness,
                LineStyle.Solid,
                Theme.ActionDisabledBackground.rgb
            )
            .transitionBackground()
            .backgroundColor(
                if (isInactive) {
                    Theme.ErrorMain.rgb
                } else {
                    when {
                        isSelected -> Theme.ActionSelected.rgb
                        isHovered -> Theme.ActionHover.rgb
                        else -> itemBackground
                    }
                }
            )
            .onClick { onRowClick() }
            .styleModifier {
                property("height", Dimen.mediumTableItemHeight.toString())
            }
            .toAttrs()
    ) {
        columns.forEach { column ->
            TableCell(
                row = row,
                column = column,
                isCustom = isCustom
            )
        }
    }
}

@Composable
private fun TableCell(
    row: Map<String, Any?>,
    column: TableColumn,
    isCustom: Boolean = false
) {
    Td(
        attrs = Modifier
            .padding(
                left = Dimen.largePadding,
                right = Dimen.largePadding
            )
            .textAlign(
                if (column.isNumeric) TextAlign.Right
                else TextAlign.Left
            )
            .overflowText()
            .styleModifier {
                if (isCustom) {
                    width((column.width ?: 100).px)
                }
            }
            .styleModifier {
                property("white-space", "pre-wrap")
            }
            .toAttrs()
    ) {
        CellContent(
            row = row,
            column = column,
            overflow = column.overflow
        )
    }
}

@Composable
private fun CellContent(
    row: Map<String, Any?>,
    column: TableColumn,
    overflow: Boolean,
) {
    if (column.customContent != null) {
        column.customContent.invoke(row).invoke()
    } else {
        val displayValue = when (column.key) {
            "category" -> {
                val categoryData = row["category"] as? Map<String, Any?>
                categoryData?.get("name")?.toString() ?: ""
            }
            else -> row[column.key]?.toString() ?: ""
        }

        SpanText(
            text = displayValue,
            modifier = Modifier
                .cursor(Cursor.Pointer)
                .minHeight(Dimen.mediumTableItemHeight)
                .fontWeight(FontWeight.Normal)
                .color(Theme.TextPrimary.rgb)
                .fontSize(FontSizes.mediumFontSize)
                .lineHeight(FontLineHeight.lineMedium)
                .letterSpacing(LetterSpacing.semiMedium)
                .fontFamily(FontFamily.Jakarta)
                .thenIf(
                    overflow,
                    Modifier.overflowText()
                )
                .thenIf(
                    !overflow,
                    Modifier
                        .styleModifier {
                            property("white-space", "pre-wrap")
                            property("word-break", "break-word")
                        }
                )
        )
    }
}

private fun getSortedData(
    data: List<Map<String, Any?>>?,
    columns: List<TableColumn>,
    sortedColumn: String?,
    sortDirection: SortDirection
): List<Map<String, Any?>>? {
    return when {
        sortedColumn == null || sortDirection == SortDirection.NONE -> data
        else -> {
            val column = columns.find { it.key == sortedColumn }
            data?.sortedWith { a, b ->
                val comparison = when {
                    column?.sortFunction != null -> {
                        compareValues(
                            column.sortFunction.invoke(a as Map<String, Any>),
                            column.sortFunction.invoke(b as Map<String, Any>)
                        )
                    }
                    column?.isNumeric == true -> {
                        val aNum = a[sortedColumn]?.toString()?.replace(",", "")?.toDoubleOrNull()
                        val bNum = b[sortedColumn]?.toString()?.replace(",", "")?.toDoubleOrNull()
                        when {
                            aNum == null && bNum == null -> 0
                            aNum == null -> -1
                            bNum == null -> 1
                            else -> aNum.compareTo(bNum)
                        }
                    }
                    else -> {
                        val aStr = a[sortedColumn]?.toString()?.lowercase() ?: ""
                        val bStr = b[sortedColumn]?.toString()?.lowercase() ?: ""
                        aStr.compareTo(bStr)
                    }
                }
                if (sortDirection == SortDirection.ASC) comparison else -comparison
            }
        }
    }
}

@Composable
private fun AlignedFooter(
    columns: List<TableColumn>,
    footerData: Map<String, String>
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(
                bottomLeft = Dimen.lowBorderRadius,
                bottomRight = Dimen.lowBorderRadius
            )
            .styleModifier {
                property("position", "sticky")
                property("bottom", "0")
                property("margin-top", "auto")
            }
    ) {
        Table(
            attrs = {
                style {
                    width(100.percent)
                    height(100.percent)
                    borderCollapse(BorderCollapse.Collapse)
                    property("table-layout", "fixed")
                }
            }
        ) {
            Tbody {
                Tr(
                    attrs = {
                        style {
                            height(Dimen.mediumGridFooterHeight)
                            backgroundColor(Theme.ActionHover.rgb)
                        }
                    }
                ) {
                    columns.forEach { column ->
                        Td(
                            attrs = {
                                style {
                                    paddingRight(Dimen.largePadding)
                                    paddingLeft(Dimen.largePadding)
                                    borderTop(
                                        Dimen.smallThickness,
                                        LineStyle.Solid,
                                        Theme.ActionDisabledBackground.rgb
                                    )
                                    textAlign(
                                        if (column.isNumeric) TextAlign.Right
                                        else TextAlign.Left
                                    )
                                    whiteSpace(WhiteSpace.NoWrap)
                                    overflow(Overflow.Hidden)
                                    textOverflow(TextOverflow.Ellipsis)
                                }
                            }
                        ) {
                            SpanText(
                                text = footerData[column.key] ?: "",
                                modifier = Modifier
                                    .cursor(Cursor.Default)
                                    .fontWeight(FontWeight.Medium)
                                    .color(Theme.TextPrimary.rgb)
                                    .fontSize(FontSizes.mediumFontSize)
                                    .lineHeight(FontLineHeight.lineMedium)
                                    .letterSpacing(LetterSpacing.semiMedium)
                                    .fontFamily(FontFamily.Jakarta)
                                    .overflowText()
                            )
                        }
                    }
                }
            }
        }
    }
}